import { environment } from '@blueberry/framework/config'
import * as Sentry from '@sentry/browser'

export type SentryConfig = {
  dsn: string
}

/**
 * Get Sentry configuration based on the current environment.
 * - In 'local' environment, Sentry is disabled.
 * - In 'staging' environment, Sentry is enabled with a high sample rate for tracing.
 * - In 'production' environment, Sentry is enabled with a lower sample rate for tracing.
 * - For any other environment, Sentry is disabled.
 * @returns Sentry.BrowserOptions - Configuration options for Sentry initialization.
 */
const getConfig = (): Sentry.BrowserOptions => {
  switch (environment) {
    case 'local': {
      return {
        enabled: false, // Disable Sentry in local development
      }
    }
    case 'staging':
      return {
        tracesSampleRate: 1.0,
        replaysOnErrorSampleRate: 0,
        replaysSessionSampleRate: 0,
      }
    case 'production':
      return {
        tracesSampleRate: 0.1,
        replaysOnErrorSampleRate: 0,
        replaysSessionSampleRate: 0,
      }
    default:
      return {
        enabled: false, // Disable Sentry for any other environment
      }
  }
}

export const initializeSentry = (config: SentryConfig) => {
  Sentry.init({
    ...getConfig(),
    dsn: config.dsn,
    environment: environment,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', 'local.blueberrypediatrics.dev'],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
  })
}
